import styled from '@emotion/styled';
import { Icon } from 'antd';
import get from 'lodash/get';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { CoachingVisitNode } from 'src/generated/webapp_gql';
import { ProviderType } from 'src/modules/user/models/user';
import {
    DEFAULT_COACHING_SESSION_DURATION,
    DEFAULT_THERAPY_SESSION_DURATION,
} from 'src/modules/videoSessions/models/videoSessions';
import { Button } from 'src/shared/components/Button';
import { Modal } from 'src/shared/components/Modal';
import { getTimeDiffFromNow } from 'src/shared/helpers/getTimeDiffFromNow';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

import { useCancelCoachingSessionMutation } from '../documents/cancelCoachingSessionMutation.generated';
import { CalendarIconContainer, CalendarIconDay, CalendarIconMonth } from './SessionsWidget';

const StyledModal = styled(Modal)({
    '.ant-modal-content': {
        borderRadius: 7.5,

        '.ant-modal-body': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '55px 0 62px',
            [mediaQueries.md]: {
                padding: '36px 0 26px',
            },
        },
    },
});

const Title = styled.h5(
    {
        color: color.pickledBluewood,
        textAlign: 'center',
        marginBottom: 10,
        marginTop: 20,
    },
    font.black(32),
);

const Description = styled.p(
    {
        color: color.steelGray,
        textAlign: 'center',
    },
    font.medium(font.size.medium),
);

const ButtonsWrapper = styled.div({
    maxWidth: 480,
    marginTop: 28,
    width: '100%',
});

interface JoinSessionTextProps {
    color: string;
}

const JoinSessionText = styled.p<JoinSessionTextProps>(
    props => ({
        color: props.color,
        textAlign: 'center',
        marginBottom: 10,
    }),
    font.heavy(font.size.medium),
);

const CancelSessionIcon = styled(Icon)({
    fontSize: font.size.medium,
    color: '#FF6E88',
});

interface Props {
    visible: boolean;
    session: CoachingVisitNode;
    onClose: () => void;
}

export const SessionDetailsModal: React.FC<Props> = ({ visible, session, onClose }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(session);
    const [loading, setLoading] = useState(false);

    const { videoSessions, user } = useDispatch<Dispatch>();

    const [cancelSession] = useCancelCoachingSessionMutation({
        client: appClient,
    });

    useEffect(() => {
        if (session) {
            setData(session);
        }
    }, [session]);

    const handleSessionCancel = async () => {
        setLoading(true);

        try {
            const response = await cancelSession({ variables: { sessionUuid } });
            const ok = get(response, 'data.cancelEvent.ok');

            if (!ok) {
                throw new Error();
            }

            await videoSessions.fetchSessions();
            // Re-fetch user info since we're getting session utilization info from `me` query
            await user.fetchUserInfo();

            toastNotificationsService.renderSuccessToast(config.messages.sessionCanceled);
        } catch (err) {
            toastNotificationsService.renderErrorToast(config.messages.genericError);
        } finally {
            setLoading(false);
            onClose();
        }
    };

    const handleSessionJoin = () => navigationService.navigate(`/video-session/${sessionUuid}`);

    const coachName = get(data, 'coachAssignment.coach.firstName');
    const providerType = get(data, 'coachAssignment.coach.providerType');
    const scheduledDuration = get(data, 'scheduledDuration'); // scheduledDuration is in minutes
    const defaultDuration =
        providerType == ProviderType.COACH
            ? DEFAULT_COACHING_SESSION_DURATION
            : DEFAULT_THERAPY_SESSION_DURATION; // defaultDuration is provided in seconds
    const visitDuration = scheduledDuration ? scheduledDuration : defaultDuration / 60; // in minutes

    const sessionUuid = get(data, 'uuid');
    const scheduledStartTime = moment(get(data, 'scheduledTime'));
    const scheduledEndTime = scheduledStartTime.clone().add(visitDuration, 'minutes');

    const timeDiff = getTimeDiffFromNow(scheduledStartTime, 'minutes');

    let sessionStartDescriptionText = '';
    let sessionStartDescriptionTextColor = '';
    let joinSessionButtonText = '';

    if (timeDiff >= 5) {
        sessionStartDescriptionText = 'session_details.not_about_to_start';
        sessionStartDescriptionTextColor = color.steelGray;
        joinSessionButtonText = 'session_details.join_anyway';
    }

    if (timeDiff < 5) {
        sessionStartDescriptionText = 'session_details.start_shortly';
        sessionStartDescriptionTextColor = color.royalBlue;
        joinSessionButtonText = 'session_details.join_anyway';
    }

    if (timeDiff < 0) {
        sessionStartDescriptionText = 'session_details.already_started';
        sessionStartDescriptionTextColor = color.royalBlue;
        joinSessionButtonText = 'session_details.join';
    }

    if (timeDiff < visitDuration * -1) {
        sessionStartDescriptionText = 'session_details.already_ended';
        sessionStartDescriptionTextColor = color.steelGray;
        joinSessionButtonText = 'session_details.join_anyway';
    }

    return (
        <StyledModal visible={visible} footer={null} width={700} onCancel={onClose} centered>
            <CalendarIconContainer>
                <CalendarIconMonth
                    style={{
                        fontSize: font.size.base,
                        paddingTop: 4,
                        borderRadius: '7px 7px 0px 0px',
                    }}
                >
                    {scheduledStartTime.format('MMM')}
                </CalendarIconMonth>
                <CalendarIconDay
                    style={{
                        fontSize: font.size.largeExtended,
                        padding: '8px 23px 6px',
                        borderRadius: '0px 0px 7px 7px',
                    }}
                >
                    {scheduledStartTime.format('D')}
                </CalendarIconDay>
            </CalendarIconContainer>

            <Title>
                {t(
                    `videoSessions:session_details.title.${
                        providerType === 'Coach' ? 'coaching' : 'therapy'
                    }`,
                    { name: coachName },
                )}
            </Title>
            <Description>
                {scheduledStartTime.format('dddd, MMM D • LT - ') + scheduledEndTime.format('LT')}
            </Description>

            <ButtonsWrapper>
                <JoinSessionText color={sessionStartDescriptionTextColor}>
                    {t(`videoSessions:${sessionStartDescriptionText}`)}
                </JoinSessionText>
                <Button onClick={handleSessionJoin} widthPercentage={100}>
                    {t(`videoSessions:${joinSessionButtonText}`)}
                </Button>

                <Button
                    type="white"
                    onClick={handleSessionCancel}
                    loading={loading}
                    widthPercentage={100}
                    marginTop={10}
                >
                    <CancelSessionIcon type="close" />
                    {t('videoSessions:session_details.cancel')}
                </Button>
            </ButtonsWrapper>
        </StyledModal>
    );
};
